import { useState } from "react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { CircleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

/**
 * What a route shows when rendering it threw. The router's default was a
 * bare heading and a Show Error button in the corner of an empty page; this
 * says what happened, offers the ways out, and keeps the stack behind a
 * disclosure for whoever needs it.
 */
export function RouteErrorBoundary({ error, reset }: ErrorComponentProps) {
  const { t } = useI18n();
  const [showDetails, setShowDetails] = useState(false);
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  return (
    <main className="route-error" role="alert">
      <div className="route-error-card">
        <span className="route-error-mark" aria-hidden="true">
          <CircleAlert size={22} />
        </span>
        <h1>{t("errors.title")}</h1>
        <p>{t("errors.description")}</p>
        <div className="route-error-actions">
          <Button onClick={reset}>{t("errors.tryAgain")}</Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            {t("errors.reload")}
          </Button>
          <Button variant="outline" asChild>
            <a href="/">{t("errors.goHome")}</a>
          </Button>
        </div>
        <Button
          variant="link"
          size="inline"
          className="route-error-toggle"
          aria-expanded={showDetails}
          onClick={() => setShowDetails((value) => !value)}
        >
          {t("errors.details")}
        </Button>
        {showDetails ? (
          <pre className="route-error-details">{stack ?? message}</pre>
        ) : null}
      </div>
    </main>
  );
}
